// @ts-ignore:next-line
import type { ActionContext, ActionTree } from 'vuex'
import type { CancelToken } from 'axios'
import type { TagState } from './types'
import type { RootState } from '@/store/modules/types'
import { clone } from '@/utils/helpers/'
import { getContext } from '@/utils/general'
import { isEmpty } from '@/utils/collection'
import { isNew, type Tag } from '@/types/resources/tag'
import type { SortDirection } from '@/types/sort'
import { requestApi } from '@/utils/requestApi'

export interface TagsResponse {
  rows: Tag[]
  count: number
}

export interface FetchMoreTagsPayload {
  /** Maximum number of tags to fetch */
  top?: number | 'all'
  /** String to filter tags by name */
  searchString?: string
  /** Array of tag OIDs to include */
  oids?: number[]
  /** Array of tag OIDs to exclude */
  notOids?: number[]
  /** Whether to reload tags or append to existing list */
  reload?: boolean
  /** Optional cancel token for the request */
  cancelToken?: CancelToken
  /** Fields to select from the tags */
  $select?: Extract<keyof Tag, string>[]
  /** Ordering of the results */
  $orderby?: [Extract<keyof Tag, string>, SortDirection?][]
}

/**
 * The fields allowed to be changed in Tag
 */
export type EditTag = Partial<Pick<Tag, 'name' | 'favourite'>>

export interface PatchTagPayload {
  /** OID of the tag to update */
  oid: number
  /** Tag data to update */
  editTag: EditTag
}

/** OIDs of the tag to delete */
export type DeleteTagsPayload = number[]

export interface AddTag {
  name: string
  favourite?: boolean
  external?: boolean
}

// TODO if we're not generating types from AIP, use [tsafe](https://github.com/garronej/tsafe) or similar to ensure AddTag matches:
//  Pick<Tag, 'name'> & Partial<Pick<Tag, 'favourite' | 'external'>>

export interface MergeTagsPayload {
  target: AddTag | Tag
  sources: number[]
}

export type GroupTagsPayload = MergeTagsPayload

// Types for each action in tagActions
export interface TagActions extends ActionTree<TagState, RootState> {
  /**
   * Fetches tags with pagination support and various filtering options
   * @param context Vuex action context
   * @param payload Payload for fetching tags
   * @returns Promise with tag data if successful, void otherwise
   */
  FETCH_TAGS (
    context: ActionContext<TagState, RootState>,
    payload: FetchMoreTagsPayload
  ): Promise<TagsResponse | undefined>

  /**
   * Updates a tag with new values
   * @param context Vuex action context
   * @param payload Payload for updating a tag
   * @returns Promise with updated tag if successful
   */
  PATCH_TAG (
    context: ActionContext<TagState, RootState>,
    payload: PatchTagPayload
  ): Promise<boolean>

  /**
   * Updates tags with new values
   * @param context Vuex action context
   * @param payloads Payload for updating tags
   * @returns Promise with updated tag if successful
   */
  PATCH_TAGS (
    context: ActionContext<TagState, RootState>,
    payloads: PatchTagPayload[]
  ): Promise<boolean>

  /**
   * Deletes many tags by their OID
   * @param context Vuex action context
   * @param payload Payload for deleting a tag
   * @returns Promise with result if successful
   */
  DELETE_TAGS (
    context: ActionContext<TagState, RootState>,
    payload: DeleteTagsPayload
  ): Promise<boolean>

  /**
   * Adds a new tag
   * @param context Vuex action context
   * @param payload Payload for adding a new tag
   * @returns Promise with the newly added tag if successful, void otherwise
   */
  ADD_TAG (
    context: ActionContext<TagState, RootState>,
    payload: AddTag
  ): Promise<Tag | undefined>

  MERGE_TAGS (
    context: ActionContext<TagState, RootState>,
    payload: MergeTagsPayload
  ): Promise<boolean>

  GROUP_TAGS (
    context: ActionContext<TagState, RootState>,
    payload: GroupTagsPayload
  ): Promise<boolean>
}

export const tagActions: TagActions = {
  async FETCH_TAGS(
    { state, commit, rootState },
    {
      top = 100,
      skip = false,
      searchString = '',
      oids = [],
      notOids = [],
      reload = false,
      $select = ['oid', 'name', 'count', 'totalCount', 'children', 'favourite', 'external'],
      $orderby = [['favourite', 'desc'], ['external', 'desc'], ['name']],
    }: FetchMoreTagsPayload,
  ) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account

    const { $axios, $arNotification } = getContext()

    // FIXME André: use filterBuilder
    let filterString
    if (searchString) {
      filterString = `name ILIKE "%${searchString}%"`
    }
    else if (oids && oids.length > 0) {
      filterString = `oid = ${oids.join(' OR oid = ')}`
    }
    else if (notOids && notOids.length > 0) {
      filterString = `oid != ${notOids.join(' AND oid != ')}`
    }

    const orderByString = $orderby.map(([sort, direction]) => {
      direction ??= 'asc'
      return [sort, direction].join(' ')
    }).join(',')

    commit('SET_IS_FETCHING_TAGS', true)

    try {
      const { data } = await $axios.get(`/promoter/${promoterOid}/tag`, {
        params: {
          $count: true,
          $select: $select.join(','),
          $orderby: orderByString,
          $top: top,
          $skip: skip ?? (reload ? 0 : state.tags.length),
          $filter: filterString || null,
        },
      })

      // Manually commit the appropriate mutation based on reload flag
      if (reload) {
        commit('SET_TAGS', data)
      }
      else {
        commit('CONCAT_TAGS', data)
      }

      commit('SET_TAGS_COUNT', data)
      commit('SET_IS_NO_MORE_TAGS', data)

      return data
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to fetch tags' })
      commit('SET_HAS_FETCH_TAGS_FAILED', true)
      throw error
    }
    finally {
      commit('SET_IS_FETCHING_TAGS', false)
    }
  },
  async PATCH_TAG(
    { commit },
    { oid, editTag },
  ) {
    commit('SET_IS_PATCHING_TAG', true) // handling this flag manually to avoid flickery loading, because when patching multiple tags, we just send this request many times.
    const clonedEditTag = clone(editTag)
    await requestApi('patchTag', {
      params: { tagOid: oid },
      body: clonedEditTag,
    })
    commit('PATCH_TAG_IN_TAGS', { oid, editTag: clonedEditTag })
    commit('SET_IS_PATCHING_TAG', false)

    // We need to manually commit the change since the API endpoint config handles mutations for tags array
    return true
  },
  async PATCH_TAGS({ commit }, payloads) {
    commit('SET_IS_PATCHING_TAG', true) // handling this flag manually to avoid flickery loading, because when patching multiple tags, we just send this request many times.
    // Use Promise.all to patch all tags in parallel
    try {
      await Promise.all(payloads.map(async (payload) => {
        const { oid, editTag } = payload
        const clonedEditTag = clone(editTag)
        await requestApi('patchTag', {
          params: { tagOid: oid },
          body: clonedEditTag,
        })
        // Manually commit each tag update
        commit('PATCH_TAG_IN_TAGS', { oid, editTag: clonedEditTag })
      }))
    }
    finally {
      commit('SET_IS_PATCHING_TAG', false)
    }
  },
  async DELETE_TAGS({ commit }, oids) {
    commit('SET_IS_DELETING_TAG', true) // handling this flag manually to avoid flickery loading, because when deleting multiple tags, we just send this request many times.
    try {
      await Promise.all(oids.map(oid =>
        requestApi('deleteTag', {
          params: { tagOid: oid },
        }),
      ))
      commit('REMOVE_TAGS_FROM_TAGS', oids)
    }
    finally {
      commit('SET_IS_DELETING_TAG', false)
    }
  },

  async ADD_TAG(
    { rootState },
    { name, favourite = false, external = false },
  ) {
    const promoterOid = rootState?.authModule?.account?.promoterOid
    const tag = await requestApi('createTag', {
      body: {
        name,
        favourite,
        external,
        promoterOid,
      },
    })
    return tag
  },

  async MERGE_TAGS(
    { commit, dispatch },
    { target, sources },
  ) {
    const tag: Tag = isNew(target)
      ? await dispatch('ADD_TAG', { name: target.name } satisfies AddTag)
      : target
    await requestApi('mergeTags', {
      params: { tagOid: tag.oid },
      body: {
        srcTagOids: sources,
      },
    })
    // Merge consolidates the source tags into the target, so drop them from the
    // local list. Done here (not via the endpoint's auto-mutation) because the
    // merge response body is a boolean, not the removed oids.
    commit('REMOVE_TAGS_FROM_TAGS', sources)
  },

  async GROUP_TAGS(
    { commit, dispatch },
    { target, sources },
  ) {
    const tag: Tag = isNew(target)
      ? await dispatch('ADD_TAG', { name: target.name } satisfies AddTag)
      : target
    await requestApi('groupTags', {
      params: { tagOid: tag.oid },
      body: {
        tagOids: sources,
      },
    })
  },
}
export default tagActions
